import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import Overlay from "components/Overlay/Overlay";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const LinguistRequirementNewEdit = () => {
  usePageTitle("Edit Linguist Requirement");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
  } = useStateContext();

  const requirement = overlayData?.requirement || null;
  const isEditMode = !!overlayData?.requirementId;

  // Edit lock for existing requirement definitions only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "linguistRequirementDefinition",
    isEditMode ? overlayData?.requirementId : null,
  );

  const [name, setName] = useState(requirement?.Name || "");
  const [description, setDescription] = useState(requirement?.Description || "");
  const [isRequiredForOnboarding, setIsRequiredForOnboarding] = useState(
    !!requirement?.IsRequiredForOnboarding,
  );
  const [isActive, setIsActive] = useState(
    requirement?.IsActive === undefined ? true : !!requirement?.IsActive,
  );

  const [saving, setSaving] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const emitSettingsSaved = (changeAction, recordId) => {
    if (!socket) return;
    socket.emit("settings:saved", {
      settingsType: "LinguistRequirementDefinition",
      recordId,
      changeAction,
      user: {
        id: currentUser?.id,
        name: currentUser?.Name,
        imageThumbnail: currentUser?.ImageThumbnail,
      },
    });
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showSnackbar("Requirement name is required", theme, "error");
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await api.put(`/linguists/requirements/${overlayData.requirementId}`, {
          Name: trimmedName,
          Description: description || "",
          IsRequiredForOnboarding: isRequiredForOnboarding,
          IsActive: isActive,
        });

        showSnackbar("Requirement updated successfully", theme, "success");
        emitSettingsSaved("Updated", overlayData.requirementId);
      } else {
        const response = await api.post("/linguists/requirements", {
          Name: trimmedName,
          Description: description || "",
          IsRequiredForOnboarding: isRequiredForOnboarding,
          IsActive: isActive,
        });

        showSnackbar("Requirement created successfully", theme, "success");
        emitSettingsSaved("Created", response?.data?.id);
      }

      overlayData?.onSaved?.();
      closeOverlay();
    } catch (err) {
      handleErrorDialog(
        err?.response?.data?.error || "Error saving requirement",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isEditMode
    ? "Edit Linguist Requirement"
    : "New Linguist Requirement";

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        <Tooltip
          title={isMobile && saving ? "Saving..." : ""}
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={saving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: "12px" }}
              >
                Save
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Overlay
        id="linguist-requirement-new-edit"
        open={true}
        onClose={closeOverlay}
        title={pageTitle}
        height="fit-content"
        footer={footerActions}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Requirement Details
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                inputProps={{ maxLength: 120 }}
                placeholder="e.g., W9 on File"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                inputProps={{ maxLength: 255 }}
                placeholder="Optional description"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                fullWidth
                variant={isRequiredForOnboarding ? "contained" : "outlined"}
                onClick={() =>
                  setIsRequiredForOnboarding((previous) => !previous)
                }
              >
                {isRequiredForOnboarding
                  ? "Onboarding Required"
                  : "Optional for Onboarding"}
              </Button>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                fullWidth
                variant={isActive ? "contained" : "outlined"}
                onClick={() => setIsActive((previous) => !previous)}
              >
                {isActive ? "Active" : "Archived"}
              </Button>
            </Grid>

            {isEditMode && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  Key: {requirement?.Key || "-"} | Source Column: {requirement?.SourceColumn || "-"}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default LinguistRequirementNewEdit;
